import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ProductNutrientEntry, ValueQualifier, emptyNutrientRow } from '../types';

interface NutritionalDataTabProps {
  nutrition: ProductNutrientEntry[];
  onChange: (nutrition: ProductNutrientEntry[]) => void;
  disabled?: boolean;
}

const toDateValue = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const NutritionalDataTab: FC<NutritionalDataTabProps> = ({ nutrition, onChange, disabled }) => {
  const { t } = useTranslation('productManagement');
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const updateRow = (index: number, patch: Partial<ProductNutrientEntry>) => {
    const next = nutrition.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const removeRow = (index: number) => {
    // Row identity is positional (key={index}); reset expanded state so a delete
    // can't leave a later row's expanded panel misattributed to its new position.
    setExpandedRows({});
    onChange(nutrition.filter((_, i) => i !== index));
  };

  const addRow = () => {
    // Expand the new row by default so mandatory fields inside the collapsed section
    // (Profile Id, Basis Type/Quantity/Unit) are visible before the user tries to save.
    setExpandedRows((prev) => ({ ...prev, [nutrition.length]: true }));
    onChange([...nutrition, emptyNutrientRow()]);
  };

  const toggleExpanded = (index: number) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="textLG"
        component="div"
        sx={{ mb: 4, fontWeight: 600 }}>
        {t('nutritionalInfo', 'Nutritional Info')}
      </Typography>

      {nutrition.length === 0 && (
        <Typography variant="textSM"
          component="div"
          color="text.secondary"
          sx={{ mb: 3 }}>
          {t('noNutritionCaptured', 'No nutritional data captured yet.')}
        </Typography>
      )}

      {nutrition.map((row, index) => {
        const isExpanded = Boolean(expandedRows[index]);
        return (
          <Box key={index}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, pt: '12px', pr: 3, pb: 0, pl: 3, mb: 3 }}>
            <Grid container
              spacing={3}
              alignItems="center">
              <Grid item
                xs={12}
                sm={3}>
                <TextField
                  label={t('nutrientCode', 'Code')}
                  value={row.nutrientCode}
                  onChange={(e) => updateRow(index, { nutrientCode: e.target.value })}
                  disabled={disabled}
                  size="small"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item
                xs={12}
                sm={3}>
                <TextField
                  label={t('nutrientName', 'Nutrient Name')}
                  value={row.nutrientName ?? ''}
                  onChange={(e) => updateRow(index, { nutrientName: e.target.value })}
                  disabled={disabled}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={5}
                sm={2}>
                <TextField
                  label={t('value', 'Value')}
                  type="number"
                  value={row.nutrientValue ?? ''}
                  onChange={(e) => updateRow(index, { nutrientValue: e.target.value === '' ? undefined : Number(e.target.value) })}
                  disabled={disabled}
                  size="small"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item
                xs={3}
                sm={2}>
                <TextField
                  label={t('unit', 'Unit')}
                  value={row.valueUom ?? ''}
                  onChange={(e) => updateRow(index, { valueUom: e.target.value })}
                  disabled={disabled}
                  size="small"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item
                xs={4}
                sm={2}
                sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton size="small"
                  onClick={() => toggleExpanded(index)}
                  aria-label={isExpanded ? t('collapse', 'Collapse') : t('expand', 'Expand')}>
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small"
                  onClick={() => removeRow(index)}
                  disabled={disabled}
                  aria-label={t('remove', 'Remove')}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>

            <Collapse in={isExpanded}>
              <Grid container
                spacing={3}
                sx={{ mt: 0, pt: 0, pb: 0, pl: '12px', pr: '12px' }}>
                <Grid item
                  xs={12}
                  sm={4}>
                  <TextField
                    label={t('nutritionProfileId', 'Profile Id')}
                    value={row.nutritionProfileId ?? ''}
                    onChange={(e) => updateRow(index, { nutritionProfileId: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <Select
                    value={row.valueQualifier ?? ''}
                    onChange={(e: SelectChangeEvent) => updateRow(index, { valueQualifier: e.target.value })}
                    displayEmpty
                    disabled={disabled}
                    size="small"
                    fullWidth
                    aria-label={t('valueQualifier', 'Value Qualifier')}
                  >
                    <MenuItem value="">
                      <em>{t('valueQualifier', 'Value Qualifier')}</em>
                    </MenuItem>
                    <MenuItem value={ValueQualifier.Exact}>{t('valueQualifierExact', 'Exact')}</MenuItem>
                    <MenuItem value={ValueQualifier.LessThan}>{t('valueQualifierLessThan', 'Less Than')}</MenuItem>
                    <MenuItem value={ValueQualifier.GreaterThan}>{t('valueQualifierGreaterThan', 'Greater Than')}</MenuItem>
                    <MenuItem value={ValueQualifier.Trace}>{t('valueQualifierTrace', 'Trace')}</MenuItem>
                    <MenuItem value={ValueQualifier.NotDetected}>{t('valueQualifierNotDetected', 'Not Detected')}</MenuItem>
                  </Select>
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('basisType', 'Basis Type')}
                    value={row.basisType ?? ''}
                    onChange={(e) => updateRow(index, { basisType: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('basisQuantity', 'Basis Quantity')}
                    type="number"
                    value={row.basisQuantity ?? ''}
                    onChange={(e) => updateRow(index, { basisQuantity: e.target.value === '' ? undefined : Number(e.target.value) })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('basisUom', 'Basis Unit')}
                    value={row.basisUom ?? ''}
                    onChange={(e) => updateRow(index, { basisUom: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('preparationState', 'Preparation State')}
                    value={row.preparationState ?? ''}
                    onChange={(e) => updateRow(index, { preparationState: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('servingSize', 'Serving Size')}
                    type="number"
                    value={row.servingSize ?? ''}
                    onChange={(e) => updateRow(index, { servingSize: e.target.value === '' ? undefined : Number(e.target.value) })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('servingUom', 'Serving Unit')}
                    value={row.servingUom ?? ''}
                    onChange={(e) => updateRow(index, { servingUom: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <TextField
                    label={t('analysisMethod', 'Analysis Method')}
                    value={row.analysisMethod ?? ''}
                    onChange={(e) => updateRow(index, { analysisMethod: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <TextField
                    label={t('sourceReference', 'Source Reference')}
                    value={row.sourceReference ?? ''}
                    onChange={(e) => updateRow(index, { sourceReference: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <TextField
                    label={t('supplierConfirmed', 'Supplier Confirmed')}
                    value={row.supplierConfirmed ?? ''}
                    onChange={(e) => updateRow(index, { supplierConfirmed: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <DatePicker
                    label={t('effectiveFrom', 'Effective From')}
                    value={toDateValue(row.effectiveFrom)}
                    onChange={(newValue) => updateRow(index, { effectiveFrom: newValue?.isValid() ? newValue.format('YYYY-MM-DD') : undefined })}
                    disabled={disabled}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <DatePicker
                    label={t('effectiveTo', 'Effective To')}
                    value={toDateValue(row.effectiveTo)}
                    onChange={(newValue) => updateRow(index, { effectiveTo: newValue?.isValid() ? newValue.format('YYYY-MM-DD') : undefined })}
                    disabled={disabled}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item
                  xs={12}>
                  <TextField
                    label={t('notes', 'Notes')}
                    value={row.notes ?? ''}
                    onChange={(e) => updateRow(index, { notes: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    multiline
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Box>
        );
      })}

      <Button startIcon={<AddIcon />}
        onClick={addRow}
        disabled={disabled}
        sx={{ mt: 2 }}>
        {t('addNutrient', 'Add nutrient')}
      </Button>
    </Box>
  );
};

export default NutritionalDataTab;
